import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { FormField, ErrorMode, CreateUserRequest, CustomerProfile, MarketingChannel, MarketingContext } from '@/models';
import { ReferAFriendLinkOpenedContentFragment } from '@/models/cms';
import {
  makeFormField,
  isValidEmail,
  isValidPhoneNumber,
  isValidCustomerName,
  makeFormFieldOfType,
  enforceMaxLength,
  stripNonNumerics,
  formatPhoneNumber,
  isValidBirthday,
  stripLeadingZeros,
  validatePassword,
  formatDate,
  parseCountryCode,
  getCountryCodeByTopLevelDomain,
  isValidUkBirthday,
  formatBirthdate,
  isValidUkPostalCode,
  reverseFormatUKPhoneNumber,
  calculateAge,
  isSafariBrowser
} from '@/helpers';
import { Routes } from '@/router';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { AnalyticsEventCategory, AnalyticsEventAction } from '@/services/google/google-analytics.services';
import { CustomerError, CustomerErrorCode } from '@/services/customer/customer.service';
import { CountryCode } from '@/store/ui';
import { persistCrossDomainData, PersistCrossDomainDataOptions, isLoyaltyEnabled } from '@/helpers';
import { AccountManagementEvent, AccountPageLoadEvent, ErrorEvent } from '@/services/analytics/events';
import { GuacModeLinkClickType, LinkClickElementType, PageLoadEventType } from '@/services/analytics/platforms/adobe-analytics/models';
import { GuacModeLinkClickEvent } from '@/services/analytics/events/guac-mode-link-click';
import { ui, countries, AUTOMATIC_PERSONALIZATION_AGE } from '@/constants'
import { CreateAccountPageLoadEvent } from '@/services/analytics/events/create-account-page-load-event';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import moment from 'moment';

export interface AccountCreatorArgs {
  request: CreateUserRequest;
  country: string;
  dateOfBirth: string;
  enrollInPersonalization?: boolean;
}

interface CreateAccountForm {
  firstName: FormField;
  lastName: FormField;
  email: FormField;
  password: FormField;
  telephone: FormField;
  birthday: FormField;
  receiveTextPromos: FormField<boolean | null>;
  receiveEmailPromos: FormField<boolean | null>;
  enrollInRewards: FormField<boolean | null>;
  enrollInPersonalization: FormField<boolean | null>;
  country: FormField;
  postalCode: FormField;
}

const getFormDefaults = (form: CreateAccountForm): CreateAccountForm => {
  form.firstName = makeFormField();
  form.lastName = makeFormField();
  form.email = makeFormField();
  form.password = makeFormField();
  form.telephone = makeFormField();
  form.birthday = makeFormField();
  form.receiveTextPromos = makeFormFieldOfType<boolean | null>(null);
  form.receiveEmailPromos = makeFormFieldOfType<boolean | null>(null);
  form.enrollInRewards = makeFormFieldOfType<boolean | null>(null);
  form.enrollInPersonalization = makeFormFieldOfType<boolean | null>(null);
  form.country = makeFormField();
  form.postalCode = makeFormField();

  return form;
};

const createAccountForm: CreateAccountForm = getFormDefaults({} as CreateAccountForm);

@Component({
  name: 'CmgCreateAccountForm',
})
export default class CmgCreateAccountForm extends Vue {
  isLoading = false;
  isFormValid = true;
  accountForm = createAccountForm;
  formSubmissionErrorMessage = '';
  signupErrorName = '';
  referAFriendLinkOpened: ReferAFriendLinkOpenedContentFragment = {};

  // a flag to toggle animation when swapping country
  showForm = true;
  // a flag to block CSS transition when form is reset after 2sv failure or cancel 
  blockCSSTransition = false;

  @Prop({ default: true, type: Boolean })
  promptSignIn!: boolean;

  @Prop({ default: true, type: Boolean })
  includeCopy!: boolean;

  // Configures if we want the basic (required) fields or the full account creation process
  @Prop({ default: false, type: Boolean })
  requiredFieldsOnly!: boolean;

  @Prop({ default: true, type: Boolean })
  navigationAlert!: boolean;

  @Prop({ type: Object })
  prefill?: CreateUserRequest;

  @Prop({ type: Function })
  accountCreator?: (args: AccountCreatorArgs) => Promise<any>;

  // need to pass this in when this form is rendered for "order confirmation" create account for last restaurant
  //ordered from rather then profile
  @Prop({ required: false, type: String })
  currentCountry?: string;

  get form(): CreateAccountForm {
    return this.accountForm;
  }

  get buttonText(): string {
    const cfValue = this.$services.config.getCreateAccountModal()?.buttonText;
    return cfValue ? cfValue : 'create an account';
  }

  get alreadyMemberText() {
    const cfValue = this.$services.config.getCreateAccountModal()?.text1;
    return cfValue ? cfValue : this.countryCode === 'CA' ? 'have an account?' : 'already a member?';
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get birthdayPlaceholder(): string {
    return this.countryCode === CountryCode.GB ? 'Birthday DD/MM' : 'Birthday MM/DD/YYYY';
  }

  get infoIcon(): string {
    return this.$appStore.state.cms.images.icons.infoOrangeBrown.default;
  }

  get telephonePlaceholder(): string {
    return this.countryCode === CountryCode.GB ? 'UK Mobile Number' : 'Mobile Number';
  }

  // On blur, the telephone number gets formatted, so
  // it requires max length of 14. When user is typing,
  // we want maxLength to be 11.
  get phoneInputMaxLength(): number {
    return this.form.telephone.formatted ? 14 : 11;
  }

  get legalCopy(): string {
    return this.$services.config.getLegalText().createAnAccount;
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isNorthAmericaCountry(): boolean {
    return this.countryCode === CountryCode.US || this.countryCode === CountryCode.CA;
  }

  get shouldShowEmailPromotionsRadioButton(): boolean {
    return !this.requiredFieldsOnly && countries[this.createAccountCountryCode.toLowerCase()].createAccountOptions.showEmailPromotionsRadioButton;
  }

  get shouldShowCountryRadioButton(): boolean {
    return countries[this.createAccountCountryCode.toLowerCase()].createAccountOptions.showCountryRadioButton;
  }

  get shouldShowBirthdayField(): boolean {
    return !this.requiredFieldsOnly && countries[this.createAccountCountryCode.toLowerCase()].createAccountOptions.showBirthdayField;
  }

  get createAccountCountryCode(): CountryCode {
    return parseCountryCode(this.form.country.value)
  }

  get subHeadingText(): string {
    if (this.isVendorCreateAccount) {
      return 'Create an account to participate in all things Chipotle Rewards';
    } else if (this.referrerCustomerIdHash && this.isRewardsReferralProgramEnabled) {
      return this.referAFriendLinkOpened.subtitle || "<p>Join Chipotle Rewards to order your favorites even faster.</p><p>Earn 100 extra points after placing your first order over $10 by signing up using this link.</p>"
    }

    const cfValue = this.$services.config.getCreateAccountModal()?.subheading;

    return cfValue ? cfValue : 'Create an account so you can get rewards and order your favorites even faster.';
  }

  get fromBag(): boolean {
    return this.$appStore.state.adobe.isFromBag;
  }

  get selectedCountryLocale() {
    return countries[this.createAccountCountryCode.toLowerCase()].locale;
  }

  get phoneNumberText() {
    const cfValue = this.$services.config.getCreateAccountModal()?.phoneDisclaimer;
    return cfValue ? cfValue : this.form.country.value === CountryCode.CA ? 'For security and order related questions.' :
      'Enter a valid mobile number that can accept SMS messages. This will be used for two-step verification for account access, and for order-related notifications.';
  }

  get isVendorCreateAccount() {
    return this.$router.currentRoute.name === Routes.VendorCreateAccount;
  }

  get isCreateAccount() {
    return this.$router.currentRoute.name === Routes.CreateAccount;
  }

  // blocks transition by returning empty string if blockCSSTransition is set to true,
  get transitionName(): string {
    return !this.blockCSSTransition ? 'slide-fade' : '';
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get isTabletOrSmaller(): boolean {
    const width = this.$appStore.state.ui.clientWidth;
    return width <= ui.breakpoints.tabletMax;
  }

  get birthday(): string {
    let bday = this.form.birthday.value ? `${formatBirthdate(this.form.birthday.value, this.form.country.value, '-', true)}` : '';
    bday = bday && !this.isNorthAmericaCountry ? moment(`${bday}-1972`, 'DD-MM-YYYY').format('MM-DD-YYYY') : bday;

    return bday;
  }

  get referrerCustomerIdHash(): string {
    return this.$appStore.state.ui.referrerCustomerIdHash || '';
  }

  get isRewardsReferralProgramEnabled(): boolean {
    return this.$appStore.state.featureFlags.featureFlags.isRewardsReferralProgramEnabled;
  }

  @Watch('isRewardsReferralProgramEnabled', { immediate: true })
  async onRewardsReferralProgramEnabledChange(newValue: boolean) {
    if (newValue) {
      const urlParams = new URLSearchParams(window.location.search);
      this.$services.ui.setReferrerCustomerIdHash(urlParams.get('cref') || '');
      await this.$services.config.setReferAFriendLinkOpened();
      this.referAFriendLinkOpened = this.$services.config.getReferAFriendLinkOpened() || {};

      if (this.referrerCustomerIdHash) {
        this.form.enrollInRewards.value = true;
      }
    }
  }

  @Watch('prefill', { immediate: true })
  onPrefillUpdated(request: CreateUserRequest | undefined) {
    this.form.country.value = this.countryCode;
    if (!request) {
      return;
    }

    this.form.firstName.value = request.firstName || '';
    this.form.lastName.value = request.lastName || '';
    this.form.email.value = request.emailAddress || '';
    this.form.password.value = request.password || '';
    this.form.telephone.value = request.phoneNumber || '';

    if (this.countryCode === CountryCode.GB) {
      this.form.telephone.value = reverseFormatUKPhoneNumber(this.form.telephone.value);
    }

    this.form.telephone.formatted = this.formatPhoneNumber(this.form.telephone.value);
  }

  async mounted() {
    await this.$services.config.setLegalText();
    if (this.currentCountry) {
      this.form.country.value = this.currentCountry;
    }

    if (this.createAccountCountryCode === CountryCode.UK) {
      this.form.receiveTextPromos.value = false;
      this.form.receiveEmailPromos.value = false;
    }

    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.UnlockGuacMode));
    }

    this.$services.analytics.send(new CreateAccountPageLoadEvent());
  }

  validateFirstName(): void {
    if (this.form.firstName.value.length == 0) {
      this.form.firstName.error = 'First Name Required';
      this.form.firstName.errorMode = ErrorMode.PlaceHolder;
    } else if (!isValidCustomerName(this.form.firstName.value)) {
      this.form.firstName.error = 'No special characters allowed';
    } else {
      this.form.firstName.error = null;
    }
  }

  validateLastName(): void {
    if (this.form.lastName.value.length == 0) {
      this.form.lastName.error = 'Last Name Required';
    } else if (!isValidCustomerName(this.form.lastName.value)) {
      this.form.lastName.error = 'No special characters allowed';
    } else {
      this.form.lastName.error = null;
    }

    this.form.lastName.errorMode = ErrorMode.PlaceHolder;
  }

  private updateFormError(message: null | string) {
    if (message && message.length > 0 && !this.signupErrorName.includes(message)) {
      this.signupErrorName = (this.signupErrorName ? this.signupErrorName + ',' : '') + message;
    }
  }

  validateEmail(): void {
    if (this.form.email.value.length == 0) {
      this.form.email.error = 'Email Required';
    } else {
      this.form.email.error = !isValidEmail(this.form.email.value) ? 'Please Enter A Valid Email' : '';

      if (this.form.email.value.includes('+') && this.countryCode !== CountryCode.GB) {
        let invalidEmailMessage = 'Please enter a valid email. ';
        let invalidEmailPlusMessage = 'Email cannot contain the \'+\' symbol.';
        if (this.isMobile) {
          this.form.email.error = invalidEmailPlusMessage;
        } else {
          this.form.email.error = invalidEmailMessage + invalidEmailPlusMessage;
        }
      }
    }
    this.form.email.errorMode = ErrorMode.PlaceHolder;
  }

  validateUKPostalCode(): void {
    if (this.form.postalCode.value.length == 0) {
      this.form.postalCode.error = 'Postcode Required';
    } else {
      this.form.postalCode.error = !isValidUkPostalCode(this.formatPostalCode(this.form.postalCode.value)) ? 'Please Enter A Valid Postcode' : '';
    }

    this.form.postalCode.errorMode = ErrorMode.PlaceHolder;
  }

  formatPostalCode(value: string): string {
    return value?.toUpperCase();
  }

  validatePassword(): void {
    validatePassword(this.form.password);
  }

  validateTelephoneNumber(): void {
    if (this.form.telephone.value.length == 0) {
      this.form.telephone.error = 'Mobile Number Required';
    } else {
      if(this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase() == 'uk' || this.$appStore.state.ui.countryProfile.countryCode.toLocaleLowerCase() == 'gb') {
        if(this.form.telephone.value.length < 10) { 
          this.form.telephone.error = 'Phone Number must contain 10 digits';
        }
        else if(this.form.telephone.value.length > 10) {
          this.form.telephone.error = 'Phone Number must contain no more than 10 digits';
        }
        else {
          this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value, this.countryCode) ? 'Please Enter A Valid Phone Number' : '';
        }
      } else {
        this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value, this.countryCode) ? 'Please Enter A Valid Phone Number' : '';
      }
    }

    this.form.telephone.errorMode = ErrorMode.PlaceHolder;
  }

  sanitizePhoneNumber(value: string): string {
    return this.countryCode === CountryCode.GB ? stripNonNumerics(value) : stripLeadingZeros(stripNonNumerics(value));
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value, this.countryCode);
  }

  validateReceiveTextPromos(): void {
    this.form.receiveTextPromos.error = this.form.receiveTextPromos.value === null ? 'Required' : null;
  }

  validateReceiveEmailPromos(): void {
    this.form.receiveEmailPromos.error = this.form.receiveEmailPromos.value === null && this.form.country.value === CountryCode.CA ? 'Required' : null;
  }

  validateEnrollInRewards(): void {
    this.form.enrollInRewards.error = this.form.enrollInRewards.value === null && this.isNorthAmericaCountry ? 'Required' : null;
  }

  validateCountry(): void {
    if (!this.form.country.value) {
      this.form.country.error = 'Please select a country';
    } else if (this.isVendorCreateAccount && this.form.country.value !== CountryCode.US) {
      this.form.country.error = 'This promotion is only available to participants in the United States';
    } else {
      this.form.country.error = '';
    }
  }

  validateBirthday(): void {
    switch (this.createAccountCountryCode) {
      case CountryCode.GB:
        // Validating DDMM
        this.form.birthday.error = !isValidUkBirthday(this.form.birthday.value) && this.form.birthday.value ? 'Please Enter A Valid Birthday DDMM' : '';
        break;
      case CountryCode.CA:
      case CountryCode.US:
        // Validating MMDDYYYY
        if (this.form.birthday.value.length == 0) {
          this.form.birthday.error = 'Birthday MM/DD/YYYY Required';
        } else {
          this.form.birthday.error = !isValidBirthday(this.form.birthday.value) && this.form.birthday.value ? 'Please enter a valid date.' : '';
        }
        break;
      default:
        // Validating MMDD
        this.form.birthday.error = !isValidBirthday(this.form.birthday.value) && this.form.birthday.value ? 'Please Enter A Valid Birthday MMDD' : '';
    }

    this.form.birthday.errorMode = ErrorMode.PlaceHolder;
  }

  formatBirthday(value: string): string {
    return this.isNorthAmericaCountry ? formatBirthdate(value, this.countryCode, '/', true) : formatDate(value);
  }

  enforceMaxLength(value: string, validLengths: number[], formField: FormField): string {
    return enforceMaxLength(value, validLengths, formField);
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }

  // hide the form when 2sv fails because otherwise the v-form-field directive does not update its bindings
  // and the form fields get in a bad validation state. 
  // Block the CSS transition because hiding and showing the form triggers a transition which isn't necessary on form reset
  hideFormAndBlockCSSTransition(): void {
    this.showForm = false;
    this.blockCSSTransition = true;
  }

  // After resetting the form we want to update the v-form-field directive bindings by showing the form after it has been hidden and reset. 
  // Resume transitions since it may have been blocked by this.hideFormAndBlockCSSTransition()
  showFormAndResumeCSSTransition(): void {
    this.$nextTick(() => {
      this.showForm = true;
      this.$nextTick(() => (this.blockCSSTransition = false));
    });
  }

  proceedAfter2svCancelledOrFailed() {
    this.hideFormAndBlockCSSTransition();

    this.resetForm();
    this.form.country.value = getCountryCodeByTopLevelDomain();

    this.showFormAndResumeCSSTransition();

    if (this.isVendorCreateAccount) {
      this.$router.push({ name: Routes.VendorSignIn });
    } else if (this.isCreateAccount) {
      this.$router.push({ name: Routes.Home });
    } else {
      this.$services.ui.setModalVisibility('signIn', false);
    }
  }

  resetForm(): void {
    // getFormDefaults will modify the accountForm instance
    // We purposely wanted to keep the same instance in order to make the transition animation work.
    getFormDefaults(this.accountForm);
    this.isFormValid = true;
  }

  validateForm() {
    this.formSubmissionErrorMessage = '';
    this.isFormValid = true;
    this.signupErrorName = '';

    this.validateFirstName();
    this.validateLastName();
    this.validateEmail();
    this.validatePassword();
    this.validateTelephoneNumber();
    this.validateReceiveTextPromos();
    this.validateReceiveEmailPromos();
    this.validateEnrollInRewards();
    this.validateCountry();
    this.validateBirthday();

    if (this.countryCode === CountryCode.GB) {
      this.validateUKPostalCode();
    }

    for (const key of Object.keys(this.form) as Array<keyof CreateAccountForm>) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
        this.updateFormError(this.form[key].error)
      }
    }
  }

  onClickInfo(event: any): void {
    this.$services.ui.pushEventTargetAsLastSelectedElement(event);
    const contentFragment = this.$services.config.getAccountOptInToolTips();

    const type = event.currentTarget.id;

    let header = '';
    let content = '';
    switch (type) {
      case 'rewardsInfo':
        header = contentFragment.rewardsTitle ||
          'Chipotle Rewards Info';
        content = contentFragment.rewardsBodyText ||
          'Enrolling in Chipotle Rewards allows you to earn points on your purchases that can be used for Chipotle food and goods.' +
          ' Rewards members also get additional offers and promotions. If you are enrolled in Chipotle Rewards and have agreed to receive texts or emails you may receive more texts and emails than if you were not enrolled in Rewards. For more information please see our <a href="/rewards-terms"><b>Rewards Terms</b></a>.';
        break;
      case 'emailMarketingInfo':
        header = contentFragment.emailTitle ||
          'Email Subscription Info';
        content = contentFragment.emailBodyText ||
          'By clicking yes you are granting Chipotle permission to send you email messages at' +
          ' the email address associated with your account regarding marketing and other promotional offers.';
        break;
      case 'smsMarketingInfo':
        header = contentFragment.smsTitle ||
          'SMS Subscription Info';
        content = contentFragment.smsBodyText ||
          'By clicking yes you are granting Chipotle permission to send you SMS (text) messages at the number' +
          ' associated with your account regarding marketing, promotional offers, and orders you may have placed. ' +
          'For more information please review our full <b><a href="/terms-of-use#mobile-terms">Mobile Terms & Conditions</a></b> section of our <b><a href="/terms-of-use">Terms of Use</a></b> to learn more.';
        break;
      default:
        break;
    }

    this.$services.ui.setModalVisibility('basicError', true, {
      header: header,
      content: content,
      options: [
        {
          text: 'OK',
          type: ButtonType.Primary,
          onClick: () => {
            this.$services.ui.setModalVisibility('basicError', false);
          },
        },
      ],
    });
    this.$services.ui.firstFocus();
  }

  onClickReceiveTextPromos(value: boolean) {
    this.form.receiveTextPromos.error = '';
    this.form.receiveTextPromos.dirty = true;
    this.form.receiveTextPromos.value = value;
  }

  onClickReceiveEmailPromos(value: boolean) {
    this.form.receiveEmailPromos.error = '';
    this.form.receiveEmailPromos.dirty = true;
    this.form.receiveEmailPromos.value = value;
  }

  onClickEnrollInRewards(value: boolean) {
    this.form.enrollInRewards.error = '';
    this.form.enrollInRewards.dirty = true;
    this.form.enrollInRewards.value = value;
  }

  onSelectCountry(value: string): void {
    if (this.form.country.value != value) {
      //reset legal text any time country changes
      if(value === CountryCode.US || value === CountryCode.CA) {
        const formattedRegion = `en-${value.toLowerCase()}`;
        this.$services.config.setLegalText(formattedRegion);
      }
      this.form.country.error = '';
      this.form.country.dirty = true;
      this.form.country.value = value;

      this.showForm = false;
      this.$nextTick(() => (this.showForm = true));

      this.$emit(
        'country-selected',
        value,
        {
          emailAddress: this.form.email.value,
          firstName: this.form.firstName.value,
          lastName: this.form.lastName.value,
          password: this.form.password.value,
          phoneNumber: this.form.telephone.value,
        },
        (country: string) => {
          this.onSelectCountry(country);
        },
      );
    }
  }

  async onCreateAccount() {
    await this.$services.config.setLegalText();
    this.isLoading = true;
    this.validateForm();

    if (!this.isFormValid) {
      this.isLoading = false;
      this.sendFormErrorAnalytics(this.signupErrorName);
      return;
    }

    if((localStorage.getItem('referredFromChipotleULandingPage') === 'true') && !this.form.enrollInRewards.value) {
      await this.showChipotleUJoinRewardsPromptModal();
    }

    const request = {
      emailAddress: this.form.email.value,
      firstName: this.form.firstName.value,
      lastName: this.form.lastName.value,
      password: this.form.password.value,
      phoneNumber: this.form.country.value === CountryCode.GB ? `44${stripLeadingZeros(this.form.telephone.value)}` : this.form.telephone.value,
      zipCode: this.formatPostalCode(this.form.postalCode.value),
      isAnonymous: false,
      isGuest: false,
      country: this.form.country.value,
      enrollInLoyalty: this.form.enrollInRewards.value!,
      enrollInEmailMarketing: this.form.country.value === CountryCode.US ? true : this.form.receiveEmailPromos.value!,
      enrollInSmsMarketing: this.form.receiveTextPromos.value ?? false,
      dateOfBirth: this.birthday
    };

    if (this.accountCreator) {
      try {
        await this.accountCreator({ request, country: this.form.country.value, dateOfBirth: formatBirthdate(this.form.birthday.value, this.form.country.value, '-', true) });
      } catch (error: any) {
        if (error instanceof CustomerError) {

          if(error.errorCode == CustomerErrorCode.PhoneInUse || error.message == CustomerErrorCode.PhoneInUse) {
            const mobileErrorMessage = 'Number already in use. Try another?';
            const desktopErrorMessage = 'This number has already been used. Please enter a new number.';
            this.form.telephone.error = this.$services.ui.isMobile() ? mobileErrorMessage : desktopErrorMessage;
            this.sendFormErrorAnalytics(this.form.telephone.error);
          } else {
            this.formSubmissionErrorMessage = error.message; 
          }
        } else {
          if(error.response.data.errorCode == CustomerErrorCode.PhoneInUse) {
            const mobileErrorMessage = 'Number already in use. Try another?';
            const desktopErrorMessage = 'This number has already been used. Please enter a new number.';
            //this.formSubmissionErrorMessage = this.$services.ui.isMobile() ? mobileErrorMessage : desktopErrorMessage; 
            this.form.telephone.dirty = true;
            this.form.telephone.error = this.$services.ui.isMobile() ? mobileErrorMessage : desktopErrorMessage;
            this.form.telephone.errorMode = ErrorMode.PlaceHolder;
          }
          else {
            this.$services.ui.setModalVisibility('globalErrorToast', true);
          }
        }
        this.sendFormErrorAnalytics(error.message);
        this.isLoading = false;
        return;
      }
    } else {
      try {
        const result = await this.createAccount(request);

        if (result == CustomerErrorCode.TwoStepVerificationRequired || result == CustomerErrorCode.AccountVerificationRequired) { // Create account throws Gigya 2 step error code
          this.$services.ui.setModalVisibility('verifyMobileNumber', true, {
            email: this.form.email.value,
            phoneNumber: this.form.telephone.value,
            oldPhoneNumber: "",
            requiresPasswordVerification: false,
            requiresSignOutOnFailure: true,
            onSuccess: async () => {
              const date = !isSafariBrowser() ? new Date(request.dateOfBirth) : new Date((moment(request.dateOfBirth, 'MM-DD-YYYY').format('YYYY-MM-DD')));
              const age = calculateAge(date);
              if (age < AUTOMATIC_PERSONALIZATION_AGE) {
                await this.showPersonalizationOptionsModal();
              } else {
                if (this.form.country.value === CountryCode.US) {
                  this.form.receiveEmailPromos.value = true;
                }
                if (this.isNorthAmericaCountry) {
                  this.form.enrollInPersonalization.value = true;
                }
                this.proceedAfterAccountCreation()
              }
            },
            onFailure: () => {
              this.proceedAfter2svCancelledOrFailed();
            }
          });
          this.$services.ui.firstFocus();
        } else if (result == CustomerErrorCode.UserAgeIneligible) {
          this.$services.ui.setModalVisibility('basicError', true, {
            header: 'AW SHUCKS',
            content: 'We aren\'t able to create your account at this time<p>Check out our <a href="/privacy-policy">Privacy Policy</a>, <a href="/rewards-terms">Rewards Terms</a>, and <a href="/terms-of-use">Terms of use</a> to learn more.',
            options: [
              {
                text: 'Return to Home',
                type: ButtonType.Primary,
                onClick: () => {
                  this.$services.ui.setModalVisibility('basicError', false);
                  this.proceedAfter2svCancelledOrFailed();
                },
              },
            ],
            onClose: () => { this.proceedAfter2svCancelledOrFailed() }
          });

          this.$services.ui.firstFocus();
        } else {
          // In current design, we should never get here, but if we do, I guess register user account when through successfully
          // No need for 2SV because Gigya wont be expecting it
          const date = !isSafariBrowser() ? new Date(request.dateOfBirth) : new Date(moment(request.dateOfBirth, 'MM-DD-YYYY').format('YYYY-MM-DD'));
          const age = calculateAge(date);
          if (age < AUTOMATIC_PERSONALIZATION_AGE) {
            await this.showPersonalizationOptionsModal();
          } else {
            if (this.form.country.value === CountryCode.US) {
              this.form.receiveEmailPromos.value = true;
            }
            this.proceedAfterAccountCreation()
          }
        }
      } catch (e: any) {
        if(e instanceof CustomerError) {
          if(e.message != CustomerErrorCode.PhoneInUse) {
            this.formSubmissionErrorMessage = e.message;
          }
        }
        return;
      }
    }
  }

  async showChipotleUJoinRewardsPromptModal(): Promise<boolean> {
    return new Promise((resolve) => {
      this.$services.ui.setModalVisibility('chipotleUJoinRewardsPromptModal', true, {
        onJoinRewards: async () => {
          this.form.enrollInRewards.value = true;
          resolve(true)
        },
        onContinueWithoutJoining: async () => {
          localStorage.removeItem('referredFromChipotleULandingPage');
          this.$services.ui.setChipotleURedirectToRewards(true);
          resolve(true)
        }
      });
    });
  }

  async showPersonalizationOptionsModal(): Promise<void> {
    this.$services.ui.setModalVisibility('personalizedContent', true, {
      showEmailMarketingOptions: this.form.country.value === CountryCode.US,
      onSuccess: async () => {
        if (this.form.country.value === CountryCode.US) {
          this.form.receiveEmailPromos.value = this.$appStore.state.customer.optedIntoEmailMarketingFromOptions;
        }
        if (this.isNorthAmericaCountry) {
          this.form.enrollInPersonalization.value = this.$appStore.state.customer.optedIntoPersonalizationMessaging;
        }
        this.proceedAfterAccountCreation();
      },
      onClose: async (link: string) => {
        await this.proceedAfterAccountCreation();
        if (link.length > 0) {
          window.location.href = link;
        }
      }
    });
  }

  async proceedAfterAccountCreation(): Promise<void> {
    const sendAnalytics = !this.isVendorCreateAccount;

    try {
      if (sendAnalytics) {
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Customer,
          action: AnalyticsEventAction.CreateAccountSuccess,
          label: document.title,
        });

        this.$services.branch.createAccountSuccessEvent();
      }

      if (!this.requiredFieldsOnly) {
        try {
          const birthday = this.form.birthday.value;
          const dateOfBirthStr = this.birthday;

          await this.$services.customer.updateCustomer({
            country: this.form.country.value,
            dateOfBirth: `${dateOfBirthStr}`,
            firstName: this.form.firstName.value,
            lastName: this.form.lastName.value,
            phoneNumber: this.form.country.value === CountryCode.GB ? `44${stripLeadingZeros(this.form.telephone.value)}` : this.form.telephone.value,
            optedIntoPersonalization: this.form.enrollInPersonalization.value,
            signupCampaign: this.$appStore.state.ui.campaignId ?? '',
            signupCampaignChannelId: this.$appStore.state.ui.campaignChannelId ?? ''
          });

          await this.createAccountSubscriptions(sendAnalytics);
        } catch (e: any) {
          this.$services.logger.error('TODO-ahhucv Error handling for update customer: ', e);
          this.isLoading = false;
        }
      }

      this.persistCrossDomainData();

      this.isLoading = false;
    } catch (e: any) {
      return;
    }

    await this.finalizeLoyaltyEnrollment();
  }

  async finalizeLoyaltyEnrollment() {
    try {
      // fetch user info
      await this.$services.customer.refreshCustomerProfileInfo();
    } catch (e: any) {
      this.$services.logger.error('Something went wrong refreshing customer info: ', undefined, e);
      return;
    }

    this.isLoading = false;
    this.postLoyaltyIdToParentFrame();

    // assign this route arg here because it will get unset when this modal gets closed
    const toRouteName = this.$appStore.state.ui.modals.signIn.args?.route ?? null;
    this.$services.ui.setModalVisibility('signIn', false);
    this.$services.ui.setModalVisibility('guacModeSignIn', false);

    this.$services.analytics.send(new AccountManagementEvent('registrationComplete'));
    this.$services.analytics.addRegistrationCompleteEventToStore({ 'value': 1 });

    if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.CreateAnAccount, LinkClickElementType.Button));
    }

    const { profile } = this.$appStore.state.customer;
    if (this.referrerCustomerIdHash && this.$services.loyalty.isEnrolledInLoyalty(profile)) {
      this.$services.ui.setModalVisibility('referralJoinSuccessModal', true);
      await this.$services.ui.waitForModalToClose('referralJoinSuccessModal');

      const shownCyobModal = this.showCyobModal();

      if(!shownCyobModal) {
        await this.showLoyaltyModal(toRouteName);
      }
    } else {
      await this.showLoyaltyModal(toRouteName);

      await this.showCyobModal();
    }
    
    this.navigateAway(toRouteName);
  }

  async onSignIn() {
    await this.$services.config.setLegalText();
    this.resetForm();
    if (this.$router.currentRoute.name == Routes.CreateAccount) {
      this.$router.push({ name: Routes.SignIn });
    } else if (this.isVendorCreateAccount) {
      this.$router.push({ name: Routes.VendorSignIn });
    } else if (this.$router.currentRoute.name === Routes.GuacModeSignIn) {
      this.$services.ui.setModalVisibility('guacModeSignIn', true);
      this.$services.analytics.send(new GuacModeLinkClickEvent(GuacModeLinkClickType.SignIn, LinkClickElementType.Link));
      this.$services.analytics.send(new AccountPageLoadEvent(PageLoadEventType.GuacModeNewAccount));
    } else {
      const route = this.$appStore.state.ui.modals.signIn.args ? this.$appStore.state.ui.modals.signIn.args.route : null;
      this.$services.ui.setModalVisibility('signIn', true, { route });
      this.$services.ui.firstFocus();
    }
    this.$emit('country-selected', this.countryCode);
  }

  async createAccount(request: CreateUserRequest): Promise<any> {
    try {
      return await this.$services.customer.registerUser(request);
    } catch (error: any) {
      if (error instanceof CustomerError) {
        if(error.message == CustomerErrorCode.PhoneInUse) {
          const mobileErrorMessage = 'Number already in use. Try another?';
          const desktopErrorMessage = 'This number has already been used. Please enter a new number.';
          this.form.telephone.error = this.$services.ui.isMobile() ? mobileErrorMessage : desktopErrorMessage;
          this.sendFormErrorAnalytics(this.form.telephone.error);
        } else {
          this.formSubmissionErrorMessage = error.message;
          this.sendFormErrorAnalytics(error.message);
        }
      } else {
        this.$services.ui.setModalVisibility('globalErrorToast', true);
      }
      this.isLoading = false;
      throw error;
    }
  }

  private sendFormErrorAnalytics(errorMessage: string | null) {
    try {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!errorMessage) {
        this.$services.analytics.send(new ErrorEvent('account', errorMessage ?? '', 'create-account'));
      }
    } catch (e: any) {
      this.$services.logger.error("Error while form validation analytics tracking ", e);
    }
  }

  private async createAccountSubscriptions(sendAnalytics: boolean): Promise<void> {
    const shouldSubscribeToMarketingSMS = this.form.receiveTextPromos.value ?? false;
    const shouldSubscribeToMarketingEmail = this.form.receiveEmailPromos.value!;
    const shouldEnrollInLoyalty = this.form.enrollInRewards.value!;
    if (shouldEnrollInLoyalty) {
      try {
        await this.$services.loyalty.enrollInLoyalty({
          customer: this.customer,
          pageTitle: document.title,
          shouldSubscribeToMarketingSMS,
          shouldSubscribeToMarketingEmail,
        });
      } catch (err) {
        this.$services.logger.error('Unable to enroll in loyalty', undefined, err);
        return;
      }
      sendAnalytics &&
        this.$services.googleAnalytics.sendCustomEvent({
          category: AnalyticsEventCategory.Rewards,
          action: AnalyticsEventAction.EnrollSuccess,
          label: document.title,
        });
    } else {
      const subscribeTo = this.$services.customer.subscribeToMarketingChannel.bind(this.$services.customer);
      const results = await Promise.allSettled([
        ...(shouldSubscribeToMarketingSMS ? [subscribeTo(MarketingChannel.Sms, MarketingContext.Marketing)] : []),
        ...(shouldSubscribeToMarketingEmail ? [subscribeTo(MarketingChannel.Email, MarketingContext.Marketing)] : []),
      ]);
      results.forEach(result => {
        if (result.status === 'rejected') {
          this.$services.logger.error('Unable to subscribe to marketing channel', undefined, result.reason);
        }
      });
    }
  }

  private persistCrossDomainData(): void {
    if (this.isVendorCreateAccount) {
      return;
    }
    const dataToPersistCrossDomain: PersistCrossDomainDataOptions = {
      data: {
        deliveryAddress: this.$appStore.state.order.selectedDeliveryAddress,
        order: this.$services.order.getCurrentOrder(),
        jwt: this.$appStore.state.customer.jwt,
      },
      sessionFrameUrl: this.$services.config.getSessionFrameUrl(),
      restaurant: this.$appStore.state.restaurant.selectedRestaurant,
      shouldRedirect: false,
      redirectQueryParams: {},
    };
    persistCrossDomainData(dataToPersistCrossDomain);
  }

  private postLoyaltyIdToParentFrame(): void {
    if (this.isVendorCreateAccount) {
      this.$services.loyalty.postLoyaltyIdToParentFrame();
    }
  }

  private async showLoyaltyModal(toRouteName: Routes | null): Promise<void> {
    const shouldNotShow = toRouteName === Routes.GroupOrderCreate || this.isVendorCreateAccount 
      || localStorage.getItem('referredFromChipotleULandingPage') === 'true';
    if (shouldNotShow) {
      return;
    }
    const { profile } = this.$appStore.state.customer;
    if (!isLoyaltyEnabled(profile?.country || '')) {
      this.$services.ui.setModalVisibility('noLoyaltyForYou', true);
    } else if (this.$services.loyalty.isEnrolledInLoyalty(profile)) {
      this.$services.ui.showOnboardingModal(this.$services.customer, this.customer ?? undefined);
      await this.$services.ui.waitForModalToClose('loyaltyExplainer');
    } else {
      if (!this.$services.config.getLoyaltyInterruptModal()) {
        await this.$services.config.setLoyaltyInterruptModal()
      }
      this.$services.ui.setModalVisibility('loyaltyExplainer', true, {
        state: LoyaltyExplainerModalState.WelcomeUnenrolled,
      });
      this.$services.ui.firstFocus();
    }
  }

  private navigateAway(toRouteName: Routes | null): void {
    const isReferralProgramRewardsRoute = !!this.referrerCustomerIdHash && Routes.Rewards.includes(this.$router.currentRoute.name as Routes);
    const shouldStay = [Routes.OrderConfirmation, Routes.VendorCreateAccount, Routes.RewardsExchange].includes(this.$router.currentRoute.name as Routes) 
      || isReferralProgramRewardsRoute;

    if (shouldStay) {
      return;
    }

    if (this.$appStore.state.ui.isFromRewards) {
      this.$router.push({ name: Routes.Rewards });
      this.$services.ui.setIsFromRewards(false);
    } else if (this.$appStore.state.ui.chipotleURedirectToRewards) {
      this.$services.ui.setChipotleURedirectToRewards(false);
      window.location.replace(`${window.location.origin}${this.$services.config.getRewardsPath()}`);
    } else {
      this.$router.push({ name: toRouteName ?? Routes.Home });
    }
  }

  
  private async showCyobModal(): Promise<boolean> {
    if (this.customer) {
      return await this.$services.ui.showCyobModal(
        this.customer,
        this.$services.reward,
        this.$services.loyalty,
        this.$services.customer,
        this.$appStore
      );
    }
    return false;
  }
}
